import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import FeedbackForm from './FeedbackForm';
import { logEvent } from '../../utils/analytics';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import './style.css';

const POSITIONS = {
  'top-left': { x: 20, y: 20 },
  'top-right': { x: window.innerWidth - 70, y: 20 },
  'bottom-left': { x: 20, y: window.innerHeight - 70 },
  'bottom-right': { x: window.innerWidth - 70, y: window.innerHeight - 70 }
};

const FeedbackButton = ({ position = 'bottom-right' }) => {
  const [showForm, setShowForm] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [feedbackType, setFeedbackType] = useState(null);
  const [positionState, setPosition] = useState(POSITIONS[position] || POSITIONS['bottom-right']);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isStuck, setIsStuck] = useState(false);
  const [isClick, setIsClick] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  const buttonRef = useRef(null);
  const optionsRef = useRef(null);

  // Set initial position based on prop
  useEffect(() => {
    if (position && POSITIONS[position]) {
      setPosition(POSITIONS[position]);
      setIsStuck(true);
    }
  }, [position]);

  // Check if user is logged in
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsLoggedIn(!!user);
    });
    
    return () => unsubscribe();
  }, []);

  const handlePositionUpdate = useCallback((newX, newY) => {
    const maxX = window.innerWidth - 70;
    const maxY = window.innerHeight - 70;
    
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (e.target === buttonRef.current || e.target.parentNode === buttonRef.current) {
      setIsDragging(true);
      setIsStuck(false);
      setIsClick(true);
      
      const rect = buttonRef.current.getBoundingClientRect();
      setShowOptions(false);
      
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  }, []);

  const handleTouchStart = useCallback((e) => {
    if (e.target === buttonRef.current || e.target.parentNode === buttonRef.current) {
      setIsDragging(true);
      setIsStuck(false);
      setIsClick(true);
      
      const rect = buttonRef.current.getBoundingClientRect();
      setShowOptions(false);
      
      const touch = e.touches[0];
      setDragOffset({
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      });
    }
  }, []);

  const handleMove = useCallback((clientX, clientY) => {
    if (isDragging) {
      const moveDistance = Math.hypot(
        clientX - (positionState.x + dragOffset.x),
        clientY - (positionState.y + dragOffset.y)
      );
      
      if (moveDistance > 5) {
        setIsClick(false);
      }
      
      handlePositionUpdate(
        clientX - dragOffset.x,
        clientY - dragOffset.y
      );
    }
  }, [isDragging, positionState, dragOffset, handlePositionUpdate]);

  const handleTouchMove = useCallback((e) => {
    const touch = e.touches[0];
    handleMove(touch.clientX, touch.clientY);
  }, [handleMove]);

  const handleMouseMove = useCallback((e) => {
    handleMove(e.clientX, e.clientY);
  }, [handleMove]);

  const handleEnd = useCallback((clientX, clientY) => {
    if (isDragging) {
      if (isClick) {
        setShowOptions(prev => !prev);
      } else {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const buttonCenterX = clientX;
        const buttonCenterY = clientY;
        
        const isLeft = buttonCenterX < viewportWidth / 2;
        const isTop = buttonCenterY < viewportHeight / 2;
        const margin = 20;
        
        if (isLeft && isTop) {
          handlePositionUpdate(margin, margin);
        } else if (!isLeft && isTop) {
          handlePositionUpdate(viewportWidth - 70 - margin, margin);
        } else if (isLeft) {
          handlePositionUpdate(margin, viewportHeight - 70 - margin);
        } else {
          handlePositionUpdate(viewportWidth - 70 - margin, viewportHeight - 70 - margin);
        }
        
        setIsStuck(true);
      }
      setIsDragging(false);
      setIsClick(false);
    }
  }, [isDragging, isClick, handlePositionUpdate]);

  const handleTouchEnd = useCallback((e) => {
    const touch = e.changedTouches[0];
    handleEnd(touch.clientX, touch.clientY);
  }, [handleEnd]);

  const handleMouseUp = useCallback((e) => {
    handleEnd(e.clientX, e.clientY);
  }, [handleEnd]);

  const handleClickOutside = useCallback((event) => {
    if (optionsRef.current && !optionsRef.current.contains(event.target) && 
        buttonRef.current && !buttonRef.current.contains(event.target)) {
      setShowOptions(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleClickOutside, handleMouseMove, handleMouseUp, handleTouchStart, handleTouchMove, handleTouchEnd]);

  const handleOpenForm = useCallback((type) => {
    setFeedbackType(type);
    setShowForm(true);
    setShowOptions(false);
    logEvent('openFeedbackForm', { feedbackType: type });
  }, []);

  const getDropdownPosition = useCallback(() => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const buttonWidth = 50;
    const buttonHeight = 50;
    const dropdownWidth = 200;
    const dropdownHeight = 150;
    
    const spaceAbove = positionState.y;
    const spaceBelow = viewportHeight - (positionState.y + buttonHeight);
    const spaceLeft = positionState.x;
    const spaceRight = viewportWidth - (positionState.x + buttonWidth);
    
    return {
      ...(spaceBelow >= dropdownHeight || spaceBelow > spaceAbove 
        ? { top: `${buttonHeight + 10}px` }
        : { bottom: `${buttonHeight + 10}px` }),
      ...(spaceRight >= dropdownWidth || spaceRight > spaceLeft
        ? { left: `${buttonWidth + 10}px` }
        : { right: `${buttonWidth + 10}px` })
    };
  }, [positionState]);

  // If user is not logged in, don't render the button
  if (!isLoggedIn) {
    return null;
  }

  return (
    <>
      <div
        ref={buttonRef}
        className={`feedback-button ${isDragging ? 'dragging' : ''} ${isStuck ? 'stuck' : ''}`}
        style={{
          left: `${positionState.x}px`,
          top: `${positionState.y}px`,
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="feedback-button-icon">
          💬
        </div>
        
        {showOptions && (
          <div 
            ref={optionsRef}
            className="feedback-options"
            style={getDropdownPosition()}
          >
            <div 
              className="feedback-option"
              onClick={() => handleOpenForm('bug')}
            >
              <span className="feedback-option-icon">🐛</span>
              <span>Report a Bug</span>
            </div>
            <div 
              className="feedback-option"
              onClick={() => handleOpenForm('other')}
            >
              <span className="feedback-option-icon">📝</span>
              <span>Other Feedback</span>
            </div>
          </div>
        )}
      </div>
      
      {showForm && (
        <FeedbackForm 
          onClose={() => setShowForm(false)} 
          feedbackType={feedbackType} 
        />
      )}
    </>
  );
};

export default FeedbackButton;